const TERMINAL_STATUSES = new Set([
  'accepted',
  'started',
  'in_progress',
  'completed',
  'cancelled',
  'canceled',
  'expired',
]);

const ACCEPT_COMPATIBLE_STATUSES = new Set(['created', 'discoverable']);

export const normalizeLocalOpsTaskStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  if (status === 'open' || status === 'available') return 'discoverable';
  return status || 'discoverable';
};

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : NaN;
};

export const getLocalOpsTaskAcceptPresentation = ({
  task,
  now = new Date(),
  isCreator = false,
  verifiedLocalLocked = false,
} = {}) => {
  if (!task) {
    return { showAccept: false, enableAccept: false, reason: 'missing_task' };
  }
  const status = normalizeLocalOpsTaskStatus(task.status);
  if (isCreator) {
    return { showAccept: false, enableAccept: false, reason: 'creator' };
  }
  if (task.acceptedPlayerId || task.acceptedAt || task.startedAt || task.completedAt) {
    return { showAccept: false, enableAccept: false, reason: 'assigned' };
  }
  if (task.cancelledAt || task.expiredAt || TERMINAL_STATUSES.has(status)) {
    return { showAccept: false, enableAccept: false, reason: 'closed' };
  }
  if (!ACCEPT_COMPATIBLE_STATUSES.has(status)) {
    return { showAccept: false, enableAccept: false, reason: 'not_open' };
  }

  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  const startMs = toTime(task?.availabilityWindow?.startAt);
  const endMs = toTime(task?.availabilityWindow?.endAt);
  if (!Number.isFinite(nowMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return { showAccept: false, enableAccept: false, reason: 'not_open' };
  }
  if (endMs !== null && endMs <= nowMs) {
    return { showAccept: false, enableAccept: false, reason: 'expired' };
  }
  if (startMs !== null && startMs > nowMs) {
    return { showAccept: false, enableAccept: false, reason: 'future' };
  }
  return {
    showAccept: true,
    enableAccept: !verifiedLocalLocked,
    reason: verifiedLocalLocked ? 'verified_local_required' : 'accept_now',
  };
};

export const buildLocalOpsAcceptFeedback = (payload = {}, task = null) => {
  const code = payload?.code || '';
  const failureReason = payload?.failureReason || '';
  if (
    code === 'PRIORITY_FIRST_LOOK_ACTIVE' ||
    code === 'FIRST_COMPLETION_ACCESS_ACTIVE'
  ) {
    const access = task?.firstCompletionAccess;
    const groups = access?.priorityGroups;
    const groupsConfirmed =
      access?.enabled === true &&
      !access.broadenedAt &&
      Array.isArray(groups) &&
      groups.length > 0 &&
      groups.every((group) =>
        ['first_completion', 'founding_helper_pass'].includes(group),
      ) &&
      (code !== 'FIRST_COMPLETION_ACCESS_ACTIVE' ||
        groups.every((group) => group === 'first_completion'));
    const hasFirstCompletion =
      groupsConfirmed && groups.includes('first_completion');
    const hasFoundingPass =
      groupsConfirmed && groups.includes('founding_helper_pass');
    const priorityHelpers =
      hasFirstCompletion && hasFoundingPass
        ? 'Founding Helpers and helpers earning their first Action Credit'
        : hasFirstCompletion
        ? 'Helpers earning their first Action Credit'
        : hasFoundingPass
        ? 'Founding Helpers'
        : '';
    // Describe the full server-supplied window, never a remaining-time estimate.
    const windowMs =
      access?.startsAt && access?.endsAt
        ? toTime(access.endsAt) - toTime(access.startsAt)
        : null;
    const headStart =
      windowMs === 4 * 60 * 1000
        ? 'a 4-minute head start'
        : 'a brief head start';
    const opening = priorityHelpers
      ? `${priorityHelpers} get ${headStart}.`
      : 'This task is in a brief early-access window.';
    return {
      kind: 'priority',
      title: 'This task opens to more helpers soon',
      message: `${opening} After that, all eligible helpers can accept if the task is still available—no pass needed.`,
      education: hasFoundingPass
        ? 'Founding Helper Pass gives eligible helpers this head start and a lower platform fee on eligible completed paid tasks.'
        : '',
    };
  }
  if (code === 'INSUFFICIENT_JGK') {
    return { message: 'Task funding is unavailable.' };
  }
  if (code === 'TRUST_GATE_REQUIRED') {
    return {
      message:
        payload?.message || 'Additional trust is required to accept this task.',
    };
  }
  if (code === 'VERIFIED_LOCAL_REQUIRED') {
    return {
      message:
        'Verified Local required. Verified Local starts at Action Credit 1, earned from verified local completions.',
    };
  }
  if (code === 'RIDE_READY_REQUIRED') {
    return {
      message:
        payload?.message ||
        'Complete Ride Ready on your Profile to accept ride-help tasks.',
      action: 'ride_ready',
      actionLabel: 'Complete Ride Ready',
      profilePath: payload?.profilePath,
    };
  }
  if (code === 'TASK_NOT_SHARED' || failureReason === 'not_shared') {
    return {
      message:
        'This task is not shared with this account. Ask the requester to select your player profile or publish to Nearby.',
    };
  }
  if (code === 'TASK_NOT_YET_AVAILABLE') {
    return {
      message:
        payload?.message || 'Task is not yet within its availability window.',
    };
  }
  if (code === 'TASK_EXPIRED') {
    return { message: payload?.message || 'Task has expired.' };
  }
  if (code === 'TASK_ALREADY_ACCEPTED' || code === 'TASK_STATE_CHANGED') {
    return {
      message:
        payload?.message ||
        'Task state changed before acceptance completed. Refresh and try another task.',
    };
  }
  if (code === 'TASK_NOT_AVAILABLE') {
    return { message: payload?.message || 'Task is not available to accept.' };
  }
  return {
    message: payload?.message || 'Unable to accept task right now.',
  };
};
